import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '../services/api-client';

// ===== Types =====

export interface WarehouseColumn {
  id: number;
  name: string;
  data_type: string;
  source_description?: string;
  local_override_description?: string;
  effective_alias?: string;
  governed_aliases?: string[];
  is_pii: boolean;
  is_hidden: boolean;
}

export interface WarehouseTable {
  id: number;
  table_external_id: string;
  name: string;
  source_description?: string;
  local_override_description?: string;
  row_count_snapshot?: number;
  last_synced_at?: string;
  is_active: boolean;
  has_drift: boolean;
  columns: WarehouseColumn[];
}

export interface WarehouseDataset {
  id: number;
  dataset_external_id: string;
  name: string;
  description?: string;
  tables: WarehouseTable[];
}

export interface WarehouseProject {
  id: number;
  project_external_id: string;
  name: string;
  sync_status: string;
  last_synced_at?: string;
  datasets: WarehouseDataset[];
}

export interface WarehouseTableListItem {
  id: number;
  project?: string | null;
  dataset?: string | null;
  name?: string | null;
  table_external_id: string;
  source_description?: string | null;
  local_override_description?: string | null;
  row_count_snapshot?: number | null;
  last_synced_at?: string | null;
  is_active: boolean;
  has_drift: boolean;
  column_count: number;
  selected: boolean;
}

export interface WarehouseTableListResponse {
  items: WarehouseTableListItem[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface WarehouseTableListParams {
  limit?: number;
  offset?: number;
  search?: string;
  dataset?: string;
  workspaceId?: number;
  enabled?: boolean;
}

export interface RegistrySyncResponse {
  status: string;
  message: string;
  project_count: number;
}

// ===== Hooks =====

/**
 * Hook to fetch the full warehouse registry for a connector.
 */
export const useGetConnectorRegistry = (connectorId: string | number) => {
  return useQuery<WarehouseProject[]>({
    queryKey: ['connector-registry', connectorId],
    queryFn: () =>
      customInstance<WarehouseProject[]>({
        url: `/api/v1/registry/connectors/${connectorId}`,
        method: 'GET',
      }),
    enabled: !!connectorId,
  });
};

/**
 * Hook to fetch a slim paginated table list for workspace table browsing.
 */
export const useGetConnectorTables = (
  connectorId: string | number,
  {
    limit = 50,
    offset = 0,
    search,
    dataset,
    workspaceId,
    enabled = true,
  }: WarehouseTableListParams = {},
) => {
  const normalizedSearch = search?.trim() || undefined;
  const normalizedDataset = dataset?.trim() || undefined;
  return useQuery<WarehouseTableListResponse>({
    queryKey: [
      'connector-registry-tables',
      connectorId,
      { limit, offset, search: normalizedSearch, dataset: normalizedDataset, workspaceId },
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      if (normalizedSearch) params.set('search', normalizedSearch);
      if (normalizedDataset) params.set('dataset', normalizedDataset);
      if (workspaceId) params.set('workspace_id', String(workspaceId));
      return customInstance<WarehouseTableListResponse>({
        url: `/api/v1/registry/connectors/${connectorId}/tables?${params.toString()}`,
        method: 'GET',
      });
    },
    enabled: enabled && !!connectorId,
    staleTime: 30_000,
    gcTime: 60_000,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook to trigger a metadata sync for a connector.
 */
export const useSyncRegistry = (connectorId: string | number) => {
  const queryClient = useQueryClient();
  return useMutation<RegistrySyncResponse, Error, void>({
    mutationFn: () =>
      customInstance<RegistrySyncResponse>({
        url: `/api/v1/registry/sync/${connectorId}`,
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connector-registry', connectorId] });
      queryClient.invalidateQueries({ queryKey: ['connector-registry-tables', connectorId] });
    },
  });
};

/**
 * Hook to update table metadata overrides.
 */
export const useUpdateTableMetadata = (tableId: number) => {
  const queryClient = useQueryClient();
  return useMutation<WarehouseTable, Error, { local_override_description?: string; is_active?: boolean }>({
    mutationFn: (data) =>
      customInstance<WarehouseTable>({
        url: `/api/v1/registry/tables/${tableId}`,
        method: 'PATCH',
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connector-registry'] });
      queryClient.invalidateQueries({ queryKey: ['connector-registry-tables'] });
    },
  });
};

/**
 * Hook to update column metadata overrides.
 */
export const useUpdateColumnMetadata = (columnId: number) => {
  const queryClient = useQueryClient();
  return useMutation<WarehouseColumn, Error, { local_override_description?: string; effective_alias?: string; governed_aliases?: string[]; is_hidden?: boolean }>({
    mutationFn: (data) =>
      customInstance<WarehouseColumn>({
        url: `/api/v1/registry/columns/${columnId}`,
        method: 'PATCH',
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connector-registry'] });
      queryClient.invalidateQueries({ queryKey: ['connector-registry-tables'] });
    },
  });
};

/**
 * Hook to fetch workspace table scope.
 */
export const useGetWorkspaceTableScope = (workspaceId: number) => {
  return useQuery<WarehouseTable[]>({
    queryKey: ['workspace-table-scope', workspaceId],
    queryFn: () =>
      customInstance<WarehouseTable[]>({
        url: `/api/v1/registry/workspaces/${workspaceId}/table-scope`,
        method: 'GET',
      }),
    enabled: !!workspaceId,
  });
};

/**
 * Hook to update workspace table scope.
 */
export const useUpdateWorkspaceTableScope = (workspaceId: number) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, { table_ids: number[] }>({
    mutationFn: (data) =>
      customInstance<any>({
        url: `/api/v1/registry/workspaces/${workspaceId}/table-scope`,
        method: 'POST',
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-table-scope', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['connector-registry-tables'] });
    },
  });
};
